import React from 'react';

const getNav = (counts = {}) => [
  { 
    section: 'Strategic Oversight', 
    items: [
      { label: 'Dashboard', icon: 'grid', page: 'dashboard' },
      { label: 'Capture', icon: 'mic', page: 'capture', dot: true },
      { label: 'Programmes', icon: 'diamond', page: 'programmes', badge: counts.programmes },
    ]
  },
  { 
    section: 'Analytics', 
    items: [
      { label: 'Insights', icon: 'triangle', page: 'insights', badge: counts.insights },
      { label: 'Milestones', icon: 'target', page: 'milestones', badge: counts.milestones },
      { label: 'Bottlenecks', icon: 'zap', page: 'bottlenecks', badge: counts.bottlenecks },
    ]
  },
  {
    section: 'Repository',
    items: [
      { label: 'Recordings', icon: 'play', page: 'recordings', badge: counts.recordings },
      { label: 'Leads', icon: 'users', page: 'leads', badge: counts.leads },
      { label: 'Lessons Learnt', icon: 'book', page: 'lessons' },
    ]
  },
];

function NavIcon({ type, size = 18 }) {
  const s = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.75 };
  switch (type) {
    case 'grid': return <svg {...s}><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>;
    case 'mic': return <svg {...s}><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/></svg>;
    case 'diamond': return <svg {...s}><path d="M12 2l8 8-8 8-8-8z"/></svg>;
    case 'triangle': return <svg {...s}><path d="M12 2L2 19.5h20L12 2z"/></svg>;
    case 'target': return <svg {...s}><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>;
    case 'zap': return <svg {...s}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" fill="none"/></svg>;
    case 'play': return <svg {...s}><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none"/></svg>;
    case 'users': return <svg {...s}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
    case 'book': return <svg {...s}><path d="M4 19.5A2.5 2.5 0 016.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z"/></svg>;
    default: return null;
  }
}

export function Topbar({ user, onLogout, onRefresh }) {
  return (
    <header className="fixed left-0 right-0 top-0 z-30 h-14 flex items-center gap-4 px-5 glass-topbar">
      {/* Logo */}
      <div className="flex items-center gap-2.5 mr-2">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: 'var(--adani-gradient)' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="white"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
        </div>
        <span className="font-google text-[15px] font-semibold text-[var(--adani-text-primary)] tracking-tight">PIP</span>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2.5 flex-1">
        {[
          ['All Time', 'This Month', 'This Quarter', 'This Year'],
          ['All Verticals', 'Energy', 'Ports', 'Defence', 'Infrastructure'],
          ['All Business Units', 'Adani Enterprises', 'Adani Ports', 'Adani Green', 'Adani Defence'],
          ['Order by Date', 'Order by Name', 'Order by Status'],
        ].map((opts, i) => (
          <select 
            key={i} 
            className="topbar-filter"
          >
            {opts.map(o => <option key={o}>{o}</option>)}
          </select>
        ))}
      </div>

      {/* Right actions */}
      <div className="flex items-center gap-5">
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="avatar avatar-sm avatar-primary text-[11px]">
              {user?.name?.[0] || 'A'}
            </div>
            <div className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full bg-[var(--adani-danger)] border-2 border-white" />
          </div>
          <span className="text-[13px] font-medium text-[var(--adani-text-primary)] hidden lg:block">
            {user?.name || 'demo'}
          </span>
        </div>

        <button onClick={onLogout} className="btn-ghost text-[13px] py-1.5 px-3 text-[var(--adani-text-tertiary)]">
          Sign out
        </button>
      </div>
    </header>
  );
}

export function Sidebar({ page, setPage, onLogout, counts = {} }) {
  const NAV = getNav(counts);
  return (
    <aside className="fixed left-0 top-0 z-20 flex h-screen w-[220px] flex-col pt-14 glass-sidebar">
      <div className="flex flex-1 flex-col overflow-y-auto px-3 pt-4 pb-4">
        {NAV.map((section, sIdx) => (
          <div key={section.section} className={sIdx > 0 ? 'mt-4 pt-4 border-t border-[var(--adani-border)]' : ''}>
            <p className="sidebar-section-label text-left">{section.section}</p>
            <nav className="flex flex-col mt-1.5">
              {section.items.map(item => {
                const active = page === item.page;
                return (
                  <button 
                    key={item.page} 
                    onClick={() => setPage(item.page)} 
                    className={`sidebar-item ${active ? 'sidebar-item-active' : ''}`}
                  >
                    {active && <div className="sidebar-active-bar" />}
                    <NavIcon type={item.icon} size={16} />
                    <span className="flex-1 text-left truncate">{item.label}</span>
                    {item.dot && <span className="w-2 h-2 rounded-full bg-[var(--adani-primary)]" />}
                    {typeof item.badge === 'number' && item.badge > 0 && (
                      <span className={`sidebar-badge ${active ? 'sidebar-badge-active' : ''}`}>
                        {item.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        ))}
      </div>
      <div style={{ padding: '16px 12px 24px', borderTop: '1px solid var(--adani-border)', textAlign: 'center' }}>
        <img src={require('../Adani_2012_logo.png')} alt="Adani" style={{ height: 42, objectFit: 'contain' }} />
      </div>
    </aside>
  );
}
